import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

interface BusinessProfile {
  name: string;
}

const LOGO_EXTENSIONS = ['png', 'svg', 'jpg', 'jpeg', 'webp'] as const;

const getInitials = (name: string): string => {
  const cleaned = name.replace(/[^a-zA-Z0-9\s]/g, ' ').trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (!words.length) return 'AA';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getLogoBaseNames = (name: string): string[] => {
  const normalized = name.trim().toLowerCase();
  const bases = new Set<string>();

  if (normalized === 'little szechuan') {
    bases.add('little-szechuan');
  }

  if (normalized.includes('live fit gym') || normalized.includes('livefit gym')) {
    bases.add('livefit-gym');
  }

  bases.add(slugify(name));
  return [...bases].filter(Boolean);
};

const getLogoCandidates = (name: string): string[] => {
  return getLogoBaseNames(name).flatMap((base) =>
    LOGO_EXTENSIONS.map((ext) => `/images/logos/${encodeURIComponent(base)}.${ext}`)
  );
};

const TrustChipIcon: React.FC<{ type: 'businesses' | 'visibility' | 'cloud' }> = ({ type }) => {
  if (type === 'businesses') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 21H21" />
        <path d="M6 21V7H18V21" />
        <path d="M10 11H14" />
        <path d="M10 15H14" />
      </svg>
    );
  }

  if (type === 'visibility') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M2 12C4.5 7.8 8 5.7 12 5.7C16 5.7 19.5 7.8 22 12C19.5 16.2 16 18.3 12 18.3C8 18.3 4.5 16.2 2 12Z" />
        <circle cx="12" cy="12" r="2.7" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6.5 18.5H17.5C19.4 18.5 21 16.95 21 15.05C21 13.2 19.52 11.68 17.7 11.6C17.16 8.98 14.84 7 12.06 7C9.73 7 7.73 8.45 6.95 10.55C4.78 10.7 3 12.51 3 14.72C3 16.82 4.65 18.5 6.5 18.5Z" />
    </svg>
  );
};

const FeatureCardIcon: React.FC<{ type: 'setup' | 'actionable' | 'network' | 'mobile' }> = ({ type }) => {
  if (type === 'setup') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 20H20" />
        <path d="M6 20V9L12 5L18 9V20" />
        <path d="M10 13H14" />
      </svg>
    );
  }

  if (type === 'actionable') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 4V13" />
        <circle cx="12" cy="18" r="1" />
        <path d="M12 3L21 20H3L12 3Z" />
      </svg>
    );
  }

  if (type === 'network') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="6.5" r="2" />
        <circle cx="6" cy="16.5" r="2" />
        <circle cx="18" cy="16.5" r="2" />
        <path d="M10.6 8.3L7.2 14.6" />
        <path d="M13.4 8.3L16.8 14.6" />
        <path d="M8 16.5H16" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="7" y="3.5" width="10" height="17" rx="2.2" />
      <path d="M10 7H14" />
      <circle cx="12" cy="17.2" r="0.9" />
    </svg>
  );
};

const BusinessLogo: React.FC<{ name: string }> = ({ name }) => {
  const logoCandidates = useMemo(() => getLogoCandidates(name), [name]);
  const [candidateIndex, setCandidateIndex] = useState(0);

  if (candidateIndex >= logoCandidates.length) {
    return <span className="home-business-logo home-business-fallback">{getInitials(name)}</span>;
  }

  return (
    <img
      src={logoCandidates[candidateIndex]}
      alt={`${name} logo`}
      className="home-business-logo"
      onError={() => setCandidateIndex((prev) => prev + 1)}
    />
  );
};

const Home: React.FC = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const businesses = useMemo<BusinessProfile[]>(
    () => [
      { name: 'LiveFit Gym - Polk Street' },
      { name: 'LiveFit Gym - Cole Valley' },
      { name: 'LiveFit Gym - LFG Elite Training' },
      { name: 'Live Fit Gym Inner Richmond' },
      { name: 'LiveFit Gym - Hayes Valley' },
      { name: 'Live Fit Gym Nob Hill' },
      { name: 'Live Fit Gym Castro' },
      { name: 'Acquolina' },
      { name: 'The Cordial' },
      { name: 'Suppenkuche' },
      { name: 'Caffe Centro SP' },
      { name: 'Tattoo Boogaloo' },
      { name: 'Rye Cocktail Bar' },
      { name: 'Rye on the Road' },
      { name: '901 Columbus Cafe' },
      { name: 'BuzzWorks' },
      { name: 'Little Szechuan' },
      { name: 'Infinite Martial Arts' },
      { name: 'Columbus Cyclery' },
      { name: 'Dream Masters Custom Tattoos' },
      { name: 'Biordi Imports' },
      { name: '101 Music' },
      { name: 'S and M Liquor Market' },
      { name: '3rd & Townsend Corner Store' },
      { name: 'Auto City Repair' },
      { name: 'Biergarten' },
      { name: 'Ralph Gracie Jiu Jitsu' },
      { name: 'Jamra Pizza' },
    ],
    []
  );

  const features = useMemo(
    () => [
      {
        title: 'Free cameras + installation',
        body: "Get free 4K+ cameras and installation from our professional installation team.",
        badge: 'Easy Setup',
        icon: 'setup' as const
      },
      {
        title: 'SFPD 911 Calls Integrated',
        body: 'See SFPD 911 calls near your storefront and decide quickly how to protect staff and customers.',
        badge: 'Actionable',
        icon: 'actionable' as const
      },
      {
        title: 'Neighborhood Coverage',
        body: 'When we partner with you, we cover the entire neighborhood around your store. Criminal activity is less likely to go undetected.',
        badge: 'Network Effect',
        icon: 'network' as const
      },
      {
        title: 'Free Mobile App',
        body: 'We built a free mobile app so you can view cameras anywhere, check on your store, and request footage.',
        badge: 'Mobile App',
        icon: 'mobile' as const
      }
    ],
    []
  );

  const softwareSteps = useMemo(
    () => [
      {
        title: 'See Incidents Around Your Store',
        body: 'Open the map to view nearby 911 calls.',
        image: '/images/crime-reported.png',
        alt: 'Map showing local incidents around a business location'
      },
      {
        title: 'Recording with 14+ Day Retention',
        body: 'AI-enabled recording keeps critical footage ready when you need it. We got your back.',
        image: '/images/ai.png',
        alt: 'Dashboard with categorized incidents'
      },
      {
        title: 'Turn Incidents Into Solved Crimes',
        body: "We'll help solve crimes faster.",
        image: '/images/solving-crimes.png',
        alt: 'Team using a real-time safety dashboard'
      }
    ],
    []
  );

  const testimonials = useMemo(
    () => [
      {
        quote:
          'We finally have one place to monitor what is happening around our block. It helps us act early instead of reacting late.',
        author: 'North Beach Retail Owner'
      },
      {
        quote:
          'Our managers check it before every evening shift. It has become part of how we run safely.',
        author: 'Mission District Store Operator'
      },
      {
        quote:
          'Simple enough for staff to use, but powerful enough to give us real situational awareness.',
        author: 'SF Multi-Location Business Team'
      }
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        question: 'I finished my install. Where is the app?',
        answer:
          'Sign in with your Gmail email after downloading the app. Google Play coming soon!',
        appStoreUrl: 'https://apps.apple.com/ca/app/archangel-ai/id6759630961',
        appStoreLabel: 'Download on the App Store'
      },
      {
        question: 'Okay, but what is the actual cost?',
        answer:
          'Seriously, it is free.'
      },
      {
        question: 'What do I need to get started?',
        answer:
          'Wifi and a power outlet. If you request cameras, our team helps with installation and onboarding.'
      },
      {
        question: 'Can I use this across multiple store locations?',
        answer:
          'Yes. You can monitor multiple locations and compare neighborhood activity so regional teams can coordinate better.'
      },
      {
        question: 'When can the installation be completed?',
        answer:
          'We can usually complete the installation within 1-2 weeks. We will work with you to find a convenient time.'
      },
      {
        question: 'How long do you keep the footage?',
        answer:
          'We aim for 14+ days. We keep all footage for 14+ days so you can review it later if needed.'
      },
      {
        question: 'Have you recorded any incidents?',
        answer:
          'We have recorded thousands of incidents and have helped solve crimes.'
      },
      {
        question: 'What if I already have cameras?',
        answer:
          'We can integrate with your existing cameras. We will work with you to find a convenient time.'
      }
    ],
    []
  );

  const carouselItems = [...businesses, ...businesses];

  return (
    <div className="home-container">
      <div className="home-hero">
        <header className="home-topbar">
          <div className="home-brand">Archangel AI</div>
          <div className="home-topbar-links">
            <Link to="/dashboard">
              Demo <span className="home-desktop-note">(desktop only)</span>
            </Link>
            <Link to="/request">Get Started</Link>
          </div>
        </header>
        <h1 className="home-offer-title">Free cameras for SF local businesses.</h1>
        <p className="home-subtitle">Protect your store with real-time neighborhood incident alerts.</p>
        <div className="home-actions">
          <Link to="/request" className="home-btn primary">Get Started</Link>

        </div>
        <div className="home-trust-chips" aria-label="Trust indicators">
          <span className="home-trust-chip">
            <span className="home-chip-icon">
              <TrustChipIcon type="businesses" />
            </span>
            <span>100+ SF businesses</span>
          </span>
          <span className="home-trust-chip">
            <span className="home-chip-icon">
              <TrustChipIcon type="visibility" />
            </span>
            <span>Live 911 visibility</span>
          </span>
          <span className="home-trust-chip">
            <span className="home-chip-icon">
              <TrustChipIcon type="cloud" />
            </span>
            <span>No hardware required to start</span>
          </span>
        </div>

        <section className="home-section home-business-section">
          <h2 className="home-business-heading">Trusted by Local Businesses</h2>
          <p className="home-section-copy home-business-copy">
            See some of our favorite partners
          </p>
          <div className="home-business-carousel" aria-label="Business types supported">
            <div className="home-business-track">
              {carouselItems.map((business, idx) => (
                <article
                  className="home-business-card"
                  key={`${business.name}-${idx}`}
                  aria-hidden={idx >= businesses.length}
                >
                  <div className="home-business-logo-wrap">
                    <BusinessLogo name={business.name} />
                  </div>
                  <p className="home-business-name">{business.name}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="home-section">
          <h2>See the Software in Action</h2>
          <div className="home-software-grid">
            {softwareSteps.map((step) => (
              <article className="home-software-card" key={step.title}>
                <img src={step.image} alt={step.alt} />
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
          <div className="home-actions home-actions-inline">
            <Link to="/request" className="home-btn primary">Get Started</Link>
          </div>
        </section>

        <section className="home-section">
          <h2>Why Owners Choose Archangel AI</h2>
          <div className="home-features-grid">
            {features.map((feature) => (
              <article className="home-feature-card" key={feature.title}>
                <div className="home-feature-meta">
                  <span className="home-feature-icon">
                    <FeatureCardIcon type={feature.icon} />
                  </span>
                  <span className="home-feature-badge">{feature.badge}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* <section className="home-section">
          <h2>What Business Operators Say</h2>
          <div className="home-testimonials">
            {testimonials.map((item) => (
              <blockquote className="home-testimonial" key={item.author}>
                <p>"{item.quote}"</p>
                <footer>{item.author}</footer>
              </blockquote>
            ))}
          </div>
        </section> */}

        <section className="home-section home-faq">
          <h2>Frequently Asked Questions</h2>
          <div className="home-faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <button
                  type="button"
                  key={faq.question}
                  className={`home-faq-item ${isOpen ? 'open' : ''}`}
                  onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                >
                  <div className="home-faq-question">
                    <span>{faq.question}</span>
                    <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                  </div>
                  {isOpen && (
                    <div className="home-faq-answer-wrap">
                      <p className="home-faq-answer">{faq.answer}</p>
                      {'appStoreUrl' in faq && faq.appStoreUrl && (
                        <a
                          href={faq.appStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="home-faq-store-link"
                        >
                          {faq.appStoreLabel || 'Download App'}
                        </a>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <section className="home-section home-final-cta">
          <h2>Ready to protect your storefront?</h2>
          <p>Get set up fast and see what is happening near your business in real time.</p>
          <div className="home-actions">
            <Link to="/request" className="home-btn primary">Get Started</Link>
          </div>
        </section>
      </div>
    
    </div>
  );
};

export default Home;


